import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ExternalRedirect } from './components/ExternalRedirect';
import { CssBaseline, ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';

import { DashboardLayout } from './layouts/DashboardLayout';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { PublicPage } from './pages/PublicPage';
import { Dashboard } from './pages/Dashboard';
import { QRPage } from './pages/QRPage';
import { UsersPage } from './pages/UsersPage';
import { ClientesPage } from './pages/ClientesPage';
import { AvisoLegalPage } from './pages/AvisoLegalPage';

// Tema personalizado con colores más modernos y configuraciones mejoradas
let theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#3f51b5', // Indigo más vibrante
      light: '#757de8',
      dark: '#002984',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f50057', // Rosa más vibrante
      light: '#ff5983',
      dark: '#bb002f',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f7f9fc', // Fondo más suave y moderno
      paper: '#ffffff',
    },
    success: {
      main: '#4caf50',
      light: '#80e27e',
      dark: '#087f23',
    },
    info: {
      main: '#2196f3',
      light: '#6ec6ff',
      dark: '#0069c0',
    },
    warning: {
      main: '#ff9800',
      light: '#ffc947',
      dark: '#c66900',
    },
    error: {
      main: '#f44336',
      light: '#ff7961',
      dark: '#ba000d',
    },
    text: {
      primary: '#263238',
      secondary: '#546e7a',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '0em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '0em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '0.0075em',
    },
    button: {
      textTransform: 'none', // Botones más modernos sin texto en mayúsculas
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8, // Bordes más redondeados para un aspecto moderno
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.08), 0px 1px 1px 0px rgba(0,0,0,0.07), 0px 1px 3px 0px rgba(0,0,0,0.06)',
    '0px 3px 3px -2px rgba(0,0,0,0.08), 0px 2px 4px 0px rgba(0,0,0,0.07), 0px 1px 8px 0px rgba(0,0,0,0.06)',
    '0px 3px 4px -2px rgba(0,0,0,0.08), 0px 3px 5px 0px rgba(0,0,0,0.07), 0px 1px 10px 0px rgba(0,0,0,0.06)',
    '0px 4px 5px -2px rgba(0,0,0,0.08), 0px 4px 5px 0px rgba(0,0,0,0.07), 0px 1px 10px 0px rgba(0,0,0,0.06)',
    '0px 5px 8px -3px rgba(0,0,0,0.08), 0px 5px 7px 0px rgba(0,0,0,0.07), 0px 2px 16px 1px rgba(0,0,0,0.06)',
    '0px 6px 10px -4px rgba(0,0,0,0.08), 0px 6px 10px 0px rgba(0,0,0,0.07), 0px 2px 16px 1px rgba(0,0,0,0.06)',
    '0px 7px 10px -4px rgba(0,0,0,0.08), 0px 7px 12px 1px rgba(0,0,0,0.07), 0px 3px 16px 2px rgba(0,0,0,0.06)',
    '0px 8px 10px -5px rgba(0,0,0,0.08), 0px 8px 12px 1px rgba(0,0,0,0.07), 0px 3px 16px 2px rgba(0,0,0,0.06)',
    '0px 9px 12px -6px rgba(0,0,0,0.08), 0px 9px 14px 1px rgba(0,0,0,0.07), 0px 4px 18px 3px rgba(0,0,0,0.06)',
    '0px 10px 14px -6px rgba(0,0,0,0.08), 0px 10px 16px 1px rgba(0,0,0,0.07), 0px 4px 18px 3px rgba(0,0,0,0.06)',
    '0px 11px 15px -7px rgba(0,0,0,0.08), 0px 11px 16px 1px rgba(0,0,0,0.07), 0px 4px 20px 3px rgba(0,0,0,0.06)',
    '0px 12px 17px -8px rgba(0,0,0,0.08), 0px 12px 18px 1px rgba(0,0,0,0.07), 0px 5px 22px 4px rgba(0,0,0,0.06)',
    '0px 13px 19px -8px rgba(0,0,0,0.08), 0px 13px 20px 1px rgba(0,0,0,0.07), 0px 5px 24px 4px rgba(0,0,0,0.06)',
    '0px 14px 21px -9px rgba(0,0,0,0.08), 0px 14px 22px 1px rgba(0,0,0,0.07), 0px 5px 26px 4px rgba(0,0,0,0.06)',
    '0px 15px 22px -9px rgba(0,0,0,0.08), 0px 15px 24px 1px rgba(0,0,0,0.07), 0px 6px 28px 5px rgba(0,0,0,0.06)',
    '0px 16px 24px -10px rgba(0,0,0,0.08), 0px 16px 26px 1px rgba(0,0,0,0.07), 0px 6px 30px 5px rgba(0,0,0,0.06)',
    '0px 17px 26px -11px rgba(0,0,0,0.08), 0px 17px 28px 1px rgba(0,0,0,0.07), 0px 7px 32px 6px rgba(0,0,0,0.06)',
    '0px 18px 28px -12px rgba(0,0,0,0.08), 0px 18px 30px 1px rgba(0,0,0,0.07), 0px 8px 34px 6px rgba(0,0,0,0.06)',
    '0px 19px 29px -12px rgba(0,0,0,0.08), 0px 19px 32px 2px rgba(0,0,0,0.07), 0px 8px 36px 7px rgba(0,0,0,0.06)',
    '0px 20px 31px -13px rgba(0,0,0,0.08), 0px 20px 34px 2px rgba(0,0,0,0.07), 0px 8px 38px 7px rgba(0,0,0,0.06)',
    '0px 21px 33px -13px rgba(0,0,0,0.08), 0px 21px 36px 2px rgba(0,0,0,0.07), 0px 9px 40px 7px rgba(0,0,0,0.06)',
    '0px 22px 35px -14px rgba(0,0,0,0.08), 0px 22px 38px 2px rgba(0,0,0,0.07), 0px 9px 42px 8px rgba(0,0,0,0.06)',
    '0px 23px 36px -14px rgba(0,0,0,0.08), 0px 23px 40px 2px rgba(0,0,0,0.07), 0px 9px 44px 8px rgba(0,0,0,0.06)',
    '0px 24px 38px -15px rgba(0,0,0,0.08), 0px 24px 42px 2px rgba(0,0,0,0.07), 0px 10px 46px 8px rgba(0,0,0,0.06)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          transition: 'box-shadow 0.3s ease',
        },
        elevation1: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'transform 0.2s ease, background-color 0.2s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
      },
    },
  },
});

// Hacer las fuentes responsivas
theme = responsiveFontSizes(theme);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/" element={<ExternalRedirect to="https://web.immunoworld.net/" />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="qr" element={<QRPage />} />
              <Route path="clientes" element={<ClientesPage />} />
              <Route 
                path="users" 
                element={
                  <ProtectedRoute allowedRoles={['admin', 'consultor']}>
                    <UsersPage />
                  </ProtectedRoute>
                } 
              />
            </Route>
            <Route path="/aviso-legal" element={<AvisoLegalPage />} />
            <Route path="/:url" element={<PublicPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
